/*
 * apiResilience — one global guard against transient API failures.
 *
 * WHY: the app makes ~198 raw `fetch()` calls to /api/ across 57 files that
 * bypass apiClient entirely, so they get NO retry. When the host returns a
 * transient 5xx (the Cloudflare 520s seen Aug 5), those screens render their
 * EMPTY state — "No packages yet", a blank workspace — which reads as data
 * loss even though the server still holds everything.
 *
 * WHAT: patches window.fetch once, at startup, and retries same-origin /api/
 * GETs on 5xx / network failure with backoff. Everything else is passed
 * straight through untouched.
 *
 * DELIBERATELY LIMITED TO SAFE-TO-REPEAT REQUESTS:
 *   - GET/HEAD only. A POST/PUT/PATCH/DELETE is NEVER retried here — replaying
 *     a write could send an email twice or double-charge. Those still surface
 *     their error to the caller, which is correct.
 *   - Same-origin /api/ only. Third-party calls are none of our business.
 *   - A request carrying a streamed/FormData body is skipped (can't replay).
 */

const RETRY_STATUSES = new Set([500, 502, 503, 504, 520, 521, 522, 523, 524]);
const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 600;

export function installApiResilience() {
  if (typeof window === 'undefined' || window.__staffedApiResilience) return;
  window.__staffedApiResilience = true;
  const nativeFetch = window.fetch.bind(window);

  const isRetryableRequest = (input, init) => {
    try {
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method !== 'GET' && method !== 'HEAD') return false;
      if (init && init.body) return false;
      const raw = typeof input === 'string' ? input : (input && input.url) || '';
      if (!raw) return false;
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return url.pathname.startsWith('/api/');
    } catch {
      return false;
    }
  };

  window.fetch = async (input, init) => {
    if (!isRetryableRequest(input, init)) return nativeFetch(input, init);
    let lastErr = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        const res = await nativeFetch(input, init);
        if (!RETRY_STATUSES.has(res.status) || attempt === MAX_ATTEMPTS) return res;
      } catch (err) {
        lastErr = err;
        if (attempt === MAX_ATTEMPTS) throw err;
      }
      await new Promise((r) => setTimeout(r, BASE_DELAY_MS * attempt));
    }
    if (lastErr) throw lastErr;
    return nativeFetch(input, init);
  };
}
